import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl } from '../utils/apiUrls';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

function DeveloperLeads() {
    const { user, isAuthenticated, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();

    const [pageSize, setPageSize] = useState(20);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [offset, setOffset] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState(null);

    // Filters
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [source, setSource] = useState('');
    const [stage, setStage] = useState('');
    const [approved, setApproved] = useState(''); // '', 'true', 'false'
    const [createdAtStart, setCreatedAtStart] = useState('');
    const [createdAtEnd, setCreatedAtEnd] = useState('');

    const isKeyFloUser = useMemo(() => {
        const email = user?.email || '';
        const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
        return ALLOWED_USERS.includes(email);
    }, [user]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (!isKeyFloUser) {
            navigate('/');
        }
    }, [isAuthenticated, isKeyFloUser, navigate]);

    const formatDateTime = (ts) => {
        try {
            if (!ts) return '';
            return new Date(ts).toLocaleString();
        } catch (e) {
            return ts || '';
        }
    };

    const buildParams = (resetOffset = true) => {
        const params = {
            limit: pageSize,
            offset: resetOffset ? 0 : offset
        };

        if (name) params.name = name;
        if (email) params.email = email;
        if (source) params.source = source;
        if (stage) params.stage = stage;
        if (approved) params.approved = approved;
        if (createdAtStart) params.created_at_start = new Date(createdAtStart).toISOString();
        if (createdAtEnd) {
            const end = new Date(createdAtEnd);
            end.setHours(23, 59, 59, 999);
            params.created_at_end = end.toISOString();
        }

        return params;
    };

    const fetchLeads = async (reset = true) => {
        if (!isAuthenticated || !isKeyFloUser) return;
        try {
            setLoading(true);
            setError('');

            const params = buildParams(reset);
            const response = await axios.get(`${BACKEND_URL}/leads/dev`, { params });

            if (response.data?.status !== 'success') {
                throw new Error(response.data?.error || 'Failed to fetch leads');
            }

            const newLeads = response.data.leads || [];
            setLeads(reset ? newLeads : [...leads, ...newLeads]);
            setTotalCount(response.data.total_count || 0);
            setOffset((reset ? 0 : offset) + newLeads.length);
        } catch (e) {
            setError(e.message || 'Failed to load leads');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && isKeyFloUser) fetchLeads(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isKeyFloUser, pageSize]);

    const handleDeleteClick = (leadId) => {
        setEntryToDelete(leadId);
        setShowDeleteModal(true);
    };

    const confirmDeleteEntry = async () => {
        if (!entryToDelete) return;
        try {
            setLoading(true);
            const response = await axios.delete(`${BACKEND_URL}/leads/dev/${entryToDelete}`);
            if (response.data?.status === 'success') {
                alert('Lead deleted successfully');
                fetchLeads(true);
            } else {
                throw new Error(response.data?.message || 'Failed to delete lead');
            }
        } catch (e) {
            alert(e.response?.data?.message || e.message || 'Failed to delete lead');
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
            setEntryToDelete(null);
        }
    };

    // Get all column names from the first record (if available)
    const getDisplayColumns = () => {
        if (leads.length === 0) return ['id', 'email', 'name', 'source', 'stage', 'approved', 'created_at', 'updated_at'];

        // Sort columns to put important ones first
        const firstLead = leads[0];
        const allCols = Object.keys(firstLead).sort();

        const priorityCols = ['id', 'email', 'name', 'approved', 'stage'];
        const otherCols = allCols.filter(c => !priorityCols.includes(c));

        return [...priorityCols, ...otherCols];
    };

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
                <p>Please log in</p>
                <button onClick={() => loginWithRedirect({
                    authorizationParams: {
                        prompt: 'login'
                    }
                })}>Log in</button>
            </div>
        );
    }

    if (!isKeyFloUser) {
        return (
            <div style={{ padding: '24px' }}>
                <h2>Access Denied</h2>
                <p>This page is restricted to KeyFlo team members.</p>
            </div>
        );
    }

    const displayColumns = getDisplayColumns();

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Marketing Leads</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                    <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
                    <label style={{ marginRight: '8px' }}>Page size:</label>
                    <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <DeveloperNavigation />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="name" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Email</label>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Source</label>
                    <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="source" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Approved</label>
                    <select
                        value={approved}
                        onChange={(e) => setApproved(e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    >
                        <option value="">Any</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Created Start</label>
                    <input type="date" value={createdAtStart} onChange={(e) => setCreatedAtStart(e.target.value)} style={{ width: '100%' }} />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={() => { setOffset(0); fetchLeads(true); }}>Apply Filters</button>
                <button onClick={() => {
                    setName('');
                    setEmail('');
                    setSource('');
                    setStage('');
                    setApproved('');
                    setCreatedAtStart('');
                    setCreatedAtEnd('');
                    setOffset(0);
                    fetchLeads(true);
                }}>Clear</button>
                <button onClick={() => fetchLeads(false)}>Load More</button>
            </div>

            {loading && <LoadingSpinner />}
            {error && (
                <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
            )}

            <div style={{ marginBottom: '12px' }}>
                Showing {leads.length} of {totalCount} leads
            </div>

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
                    <thead>
                        <tr>
                            <th style={{
                                textAlign: 'left',
                                padding: '10px 12px',
                                borderBottom: '1px solid #e5e7eb',
                                background: '#f8fafc',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                            }}>
                                Actions
                            </th>
                            {displayColumns.map((col) => (
                                <th
                                    key={col}
                                    style={{
                                        textAlign: 'left',
                                        padding: '10px 12px',
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#f8fafc',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1
                                    }}
                                >
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {leads.map((item, idx) => (
                            <tr key={item.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '60px' }}>
                                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                                    <button
                                        onClick={() => handleDeleteClick(item.id)}
                                        style={{
                                            backgroundColor: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Delete
                                    </button>
                                </td>
                                {displayColumns.map((col) => {
                                    let val = item[col];

                                    // Format dates
                                    if (col.includes('_at') || col.includes('date')) {
                                        val = formatDateTime(val);
                                    }

                                    // Format boolean values
                                    if (typeof val === 'boolean') {
                                        val = val ? 'true' : 'false';
                                    }

                                    if (val === null || typeof val === 'undefined') val = '';

                                    return (
                                        <td
                                            key={col}
                                            style={{
                                                padding: '10px 12px',
                                                borderBottom: '1px solid #f1f5f9',
                                                borderRight: '1px solid #f1f5f9',
                                                verticalAlign: 'top',
                                                whiteSpace: 'pre-wrap',
                                                minWidth: 120,
                                                maxWidth: 300
                                            }}
                                        >
                                            <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                                                {String(val)}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {leads.length === 0 && !loading && (
                            <tr>
                                <td colSpan={displayColumns.length} style={{ padding: '12px', textAlign: 'center' }}>No leads found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showDeleteModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '400px', maxWidth: '90%' }}>
                        <h3 style={{ marginTop: 0, color: '#ef4444' }}>Confirm Delete</h3>
                        <p>Are you sure you want to delete this lead? This action cannot be undone.</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                            <button
                                onClick={() => { setShowDeleteModal(false); setEntryToDelete(null); }}
                                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteEntry}
                                disabled={loading}
                                style={{ background: '#ef4444', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px' }}
                            >
                                {loading ? 'Deleting...' : 'Delete Lead'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DeveloperLeads;
